import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Star, Filter, Stethoscope, Briefcase, Gavel, Building, Microscope, GraduationCap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { useEffect, useMemo } from 'react';

const AgentCard = ({ 
  id,
  name, 
  description, 
  category, 
  rating, 
  uses, 
  icon: Icon,
  standard = false 
}) => {
  const navigate = useNavigate();
  
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      <CardHeader className="pb-4">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-md text-primary">
              <Icon size={24} />
            </div>
            <CardTitle className="text-lg">{name}</CardTitle>
          </div>
          {standard && (
            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-yellow-300">
              Standard
            </Badge>
          )}
        </div>
        <CardDescription className="line-clamp-2">{description}</CardDescription>
      </CardHeader>
      <CardContent className="pb-4">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Star size={14} className="fill-yellow-400 text-yellow-400" />
            <span>{rating}/5</span>
          </div>
          <div>
            <span>{uses.toLocaleString()} users</span>
          </div>
          <Badge variant="secondary" className="capitalize">
            {category}
          </Badge>
        </div>
      </CardContent>
      <CardFooter className="border-t pt-4 flex gap-2">
        <Button 
          variant="default" 
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"

          onClick={() => navigate(`/agents/use/${id}`)}
        >
          Use Agent
        </Button>
      </CardFooter>
    </Card>
  );
};

const ProfessionalAgents = () => {
  const { agents, fetchAgents } = useSupabaseStore();

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const professionalCategories = ["healthcare", "legal", "business", "education", "finance"];
  const professionalAgents = useMemo(() => 
    agents.filter(a => a.is_standard && professionalCategories.includes(a.domain)), 
    [agents]
  );

  const iconMap = {
    Stethoscope, Briefcase, Gavel, Building, Microscope, GraduationCap
  };

  return (
    <MainLayout>
      <div className="container px-4 py-6 max-w-7xl mx-auto">
        <div className="flex flex-col space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Professional AI Agents
              </h1>
              <p className="text-muted-foreground mt-1">
                Expert AI agents for professional domains and specialized knowledge
              </p>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative w-full md:w-96">
              <Input
                placeholder="Search professional agents..."
                className="pl-10"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
            </div>
            
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" className="gap-2">
                <Filter size={16} />
                Filters
              </Button>
            </div>
          </div>
          
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="healthcare">Healthcare</TabsTrigger>
              <TabsTrigger value="legal">Legal</TabsTrigger>
              <TabsTrigger value="business">Business</TabsTrigger>
              <TabsTrigger value="education">Education</TabsTrigger>
              <TabsTrigger value="finance">Finance</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {professionalAgents.map((agent) => (
                  <AgentCard key={agent.id} {...agent} category={agent.domain} icon={iconMap[agent.icon]} standard={agent.is_standard} />
                ))}
              </div>
            </TabsContent>
            
            {professionalCategories.map((category) => (
              <TabsContent key={category} value={category} className="mt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {professionalAgents
                    .filter(agent => agent.domain === category)
                    .map((agent) => (
                      <AgentCard key={agent.id} {...agent} category={agent.domain} icon={iconMap[agent.icon]} standard={agent.is_standard} />
                    ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </MainLayout>
  );
};

export default ProfessionalAgents;